import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import HeaderLogged from '../components/HeaderLogged';
import Footer from '../components/Footer';
import { useWizardFormId, withForm } from '../utils/wizardForm';

function IrsPaymentOptions8849() {
  const { id } = useParams();
  const navigate = useNavigate();
  // Номер формы — из адреса этой вкладки (см. utils/wizardForm.js)
  const formId = useWizardFormId();

  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedOption, setSelectedOption] = useState('');

  // Card fields
  const [cardNumber, setCardNumber] = useState('');
  const [cvc, setCvc] = useState('');
  const [cvv, setCvv] = useState('');

  // Validation errors
  const [errors, setErrors] = useState({ cardNumber: '', cvc: '', cvv: '' });
  const [submitAttempted, setSubmitAttempted] = useState(false);

  useEffect(() => {
    async function fetchCompanyData() {
      try {
        const token = localStorage.getItem('token');
        const res = await fetch(`/api/companies/${id}/`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) throw new Error('Error loading company data');
        setCompany(await res.json());
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    }
    fetchCompanyData();
  }, [id]);

  const handleOptionChange = (opt) => {
    setSelectedOption(opt);
    if (opt !== 'CCDC') {
      setCardNumber('');
      setCvc('');
      setCvv('');
      setErrors({ cardNumber: '', cvc: '', cvv: '' });
      setSubmitAttempted(false);
    }
  };

  // Populate errors and return validity
  const validateErrors = () => {
    const errs = { cardNumber: '', cvc: '', cvv: '' };
    if (selectedOption === 'CCDC') {
      if (!/^\d{16}$/.test(cardNumber)) errs.cardNumber = 'Card number must be 16 digits';
      if (!/^\d{3}$/.test(cvc)) errs.cvc = 'CVC must be 3 digits';
      if (!/^\d{3}$/.test(cvv)) errs.cvv = 'CVV must be 3 digits';
    }
    setErrors(errs);
    return Object.values(errs).every((e) => e === '');
  };

  // For disabling button
  const isCardInfoValid = () => {
    if (selectedOption !== 'CCDC') return true;
    return (
      /^\d{16}$/.test(cardNumber) &&
      /^\d{3}$/.test(cvc) &&
      /^\d{3}$/.test(cvv)
    );
  };

  const handleContinueFiling = async () => {
    if (selectedOption === 'CCDC' && !validateErrors()) {
      setSubmitAttempted(true);
      return;
    }

    try {
      const token = localStorage.getItem('token');
      if (!formId) throw new Error('Form ID not found');

      const payment_info =
        selectedOption === 'CCDC'
          ? JSON.stringify({ card_number: cardNumber, cvc, cvv })
          : null;

      const res = await fetch(`/api/form8849/${formId}/`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          irs_payment_option: selectedOption,
          payment_info,
          step: 4,  // следующий шаг — Form Summary
        }),
      });
      if (!res.ok) throw new Error('Error updating payment option');
      navigate(withForm(`/company/${id}/file-form/form-summary-8849`, formId));
    } catch (e) {
      console.error(e);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen bg-[#E4F2FF]">
        <p className="text-gray-700 text-lg">Loading...</p>
      </div>
    );
  }

  const steps = [
    { id: 1, label: 'Tax & Form' },
    { id: 2, label: 'Vehicles Tax' },
    { id: 3, label: 'IRS Payment Options' },
    { id: 4, label: 'Form Summary' },
    { id: 5, label: 'Payment and IRS Submission' },
    { id: 6, label: 'IRS Confirmation' },
  ];
  const currentStep = 3;

  return (
    <div className="flex flex-col min-h-screen">
      <HeaderLogged />

      {/* Progress bar */}
      <div className="bg-white shadow p-4">
        <div className="flex flex-wrap justify-center gap-2">
          {steps.map((step) => (
            <div
              key={step.id}
              className={`px-2 py-1 sm:px-4 sm:py-2 rounded border text-center text-xs sm:text-sm ${
                step.id === currentStep
                  ? 'bg-blue-600 text-white border-blue-600'
                  : 'bg-gray-300 text-gray-700 border-gray-300'
              }`}
            >
              {step.label}
            </div>
          ))}
        </div>
      </div>

      {/* Main content */}
      <main className="flex-1 bg-[#E4F2FF] p-4 flex justify-center items-start">
        <div className="bg-white rounded shadow p-4 sm:p-6 w-full max-w-4xl space-y-6">
          <h1 className="text-2xl font-bold text-[#0F2C5E]">IRS Payment Options</h1>
          <p className="text-gray-700">
            Authorize a direct IRS debit from your bank account, or choose another payment method.
          </p>

          <div className="space-y-4">
            {/* 1. Direct Debit */}
            <label className="flex items-start space-x-2 cursor-pointer">
              <input
                type="radio"
                name="paymentOption"
                className="mt-1 h-5 w-5 text-[#0F2C5E]"
                value="EFW"
                checked={selectedOption === 'EFW'}
                onChange={() => handleOptionChange('EFW')}
              />
              <span className="text-gray-800">
                <strong>Electronic Funds Withdrawal (Direct Debit)</strong><br/>
                Authorize IRS to debit from your bank account.
              </span>
            </label>

            {/* 2. EFTPS */}
            <label className="flex items-start space-x-2 cursor-pointer">
              <input
                type="radio"
                name="paymentOption"
                className="mt-1 h-5 w-5 text-[#0F2C5E]"
                value="EFTPS"
                checked={selectedOption === 'EFTPS'}
                onChange={() => handleOptionChange('EFTPS')}
              />
              <span className="text-gray-800">
                <strong>EFTPS - Electronic Federal Tax Payment System</strong><br/>
                Pay electronically via the IRS EFTPS portal{' '}
                <a
                  href="https://www.eftps.gov"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 underline"
                >
                  eftps.gov
                </a>
              </span>
            </label>

            {/* 3. Credit/Debit Card */}
            <label className="flex items-start space-x-2 cursor-pointer">
              <input
                type="radio"
                name="paymentOption"
                className="mt-1 h-5 w-5 text-[#0F2C5E]"
                value="CCDC"
                checked={selectedOption === 'CCDC'}
                onChange={() => handleOptionChange('CCDC')}
              />
              <span className="text-gray-800">
                <strong>Credit Card / Debit Card</strong><br/>
                Pay using your credit or debit card via the IRS payments portal{' '}
                <a
                  href="https://www.irs.gov/payments"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 underline"
                >
                  irs.gov/payments
                </a>
              </span>
            </label>

            {/* Card form */}
            {selectedOption === 'CCDC' && (
              <div className="mt-4 p-4 border border-gray-300 rounded-lg bg-white">
                {submitAttempted && !isCardInfoValid() && (
                  <div className="mb-4 bg-red-50 border border-red-400 text-red-700 p-3 rounded">
                    {errors.cardNumber && <p>Please check card number.</p>}
                    {errors.cvc && <p>Invalid CVC.</p>}
                    {errors.cvv && <p>Invalid CVV.</p>}
                  </div>
                )}

                <div>
                  <label className="block text-[#0F2C5E] font-medium">Card Number</label>
                  <input
                    type="text"
                    maxLength={16}
                    placeholder="1234567812345678"
                    value={cardNumber}
                    onChange={(e) => setCardNumber(e.target.value.replace(/\D/g, ''))}
                    className={`mt-1 block w-full p-3 border rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-[#E4F2FF] focus:border-[#0F2C5E] ${errors.cardNumber ? 'border-red-500' : ''}`}
                  />
                </div>

                <div className="flex space-x-4">
                  <div className="flex-1">
                    <label className="block text-[#0F2C5E] font-medium">CVC</label>
                    <input
                      type="text"
                      maxLength={3}
                      placeholder="123"
                      value={cvc}
                      onChange={(e) => setCvc(e.target.value.replace(/\D/g, ''))}
                      className={`mt-1 block w-full p-3 border rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-[#E4F2FF] focus:border-[#0F2C5E] ${errors.cvc ? 'border-red-500' : ''}`}
                    />
                  </div>
                  <div className="flex-1">
                    <label className="block text-[#0F2C5E] font-medium">CVV</label>
                    <input
                      type="text"
                      maxLength={3}
                      placeholder="456"
                      value={cvv}
                      onChange={(e) => setCvv(e.target.value.replace(/\D/g, ''))}
                      className={`mt-1 block w-full p-3 border rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-[#E4F2FF] focus:border-[#0F2C5E] ${errors.cvv ? 'border-red-500' : ''}`}
                    />
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Buttons */}
          <div className="flex flex-col sm:flex-row justify-between mt-8 gap-4">
            <button
              className="bg-gray-300 text-gray-700 px-4 py-2 rounded hover:bg-gray-400 transition w-full sm:w-auto"
              onClick={() => navigate(withForm(`/company/${id}/file-form/vehicles-tax-8849`, formId))}
            >
              Back
            </button>
            <button
              className="bg-[#0F2C5E] text-white px-4 py-2 rounded hover:bg-blue-900 transition w-full sm:w-auto disabled:opacity-50"
              onClick={handleContinueFiling}
              disabled={!selectedOption || (selectedOption === 'CCDC' && !isCardInfoValid())}
            >
              Continue Filing
            </button>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}

export default IrsPaymentOptions8849;
